import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Formats an ISBN to ensure it's never displayed in scientific notation.
 * Explicitly converts to string and removes any scientific notation formatting.
 */
export function formatIsbn(isbn: string | number | null | undefined): string {
  if (isbn === null || isbn === undefined || isbn === '') {
    return 'N/A';
  }
  // Convert to string and ensure no scientific notation
  return String(isbn);
}

/**
 * Returns true if the ISBN was auto-generated by the system for a book that
 * has no real ISBN. Format: "BB-XXXXXXXX" (8 hex chars after the prefix).
 * These values are kept in books.isbn so search, lending, returns, and
 * barcode printing all key off the same field uniformly.
 */
export function isGeneratedIsbn(isbn: string | null | undefined): boolean {
  if (!isbn) return false;
  return /^BB-[0-9A-Fa-f]{8}$/.test(isbn.trim());
}

// Pre-compiled regex matching Unicode combining-diacritic marks (U+0300–U+036F).
// Built via String.fromCharCode + new RegExp so the source file stays ASCII.
const DIACRITICS_REGEX = new RegExp(
  '[' + String.fromCharCode(0x0300) + '-' + String.fromCharCode(0x036f) + ']',
  'g'
);

/**
 * Normalizes a string for accent-insensitive, case-insensitive search.
 * Strips diacritics (so "qué" becomes "que", "niño" becomes "nino", etc.)
 * and lowercases. Use this on BOTH the query and the candidate text so a
 * search for "que" matches stored "qué", and vice versa.
 */
export function normalizeForSearch(value: string | null | undefined): string {
  if (!value) return '';
  return value.normalize('NFD').replace(DIACRITICS_REGEX, '').toLowerCase();
}
